import React, { useMemo } from "react";
import { Field } from "react-final-form";

import TextInput from "../../inputs/TextInput";
import RffInputWrapper from "../shared/RffInputWrapper";

import { VALIDATOR_REQUIRE } from "../../../utils/finalForm/validators/VALIDATOR_REQUIRE";

const RffFloatField = ({
  name,
  label,
  required = false,
  placeholder = "Enter value",
  suppressGrid,
  disabled,
  onBlur,
  size,
  sx,
  suffix, // Optional suffix like "x" for DSCR
  prefix, // Optional prefix like "$" for dollar amounts
  precision = 2,
  helperText,
  min, // <-- new
  max, // <-- new
}) => {
  // Combined validator: required, min, max
  const validate = useMemo(() => {
    return (value) => {
      // Required check
      if (required && (value === undefined || value === null || value === "")) {
        return VALIDATOR_REQUIRE(value);
      }

      // Skip bounds if empty or non-numeric-in-progress
      const num = typeof value === "number" ? value : parseFloat(String(value));
      if (isNaN(num)) return undefined;

      // Min/max checks
      if (min !== undefined && num < min) {
        return `${label} must be at least ${prefix ?? ""}${min}${suffix ?? ""}`;
      }
      if (max !== undefined && num > max) {
        return `${label} must be at most ${prefix ?? ""}${max}${suffix ?? ""}`;
      }

      return undefined;
    };
  }, [required, min, max, label, prefix, suffix]);

  const formatFloat = (value) => {
    if (value === undefined || value === null || value === "") return "";

    const str = String(value);
    // preserve in-progress typing
    if (str.endsWith(".") || str.endsWith(".0") || str.endsWith("-.")) {
      return str;
    }

    const n = parseFloat(value);
    if (isNaN(n)) return value;

    let formatted = n.toFixed(precision).replace(/\.?0+$/, "");

    if (prefix) formatted = `${prefix}${formatted}`;
    if (suffix) formatted = `${formatted}${suffix}`;

    return formatted;
  };

  const parseFloatValue = (value) => {
    if (value == null || value === "") return undefined;

    let v = String(value).trim();
    if (prefix && v.startsWith(prefix)) {
      v = v.slice(prefix.length);
    }
    if (suffix && v.endsWith(suffix)) {
      v = v.slice(0, -suffix.length);
    }
    v = v.replace(/[^0-9.-]/g, "");

    // in-progress typing
    if (["", ".", "-", "-."].includes(v) || v.endsWith(".")) {
      return v;
    }

    const p = parseFloat(v);
    return isNaN(p) ? v : p;
  };

  return (
    <RffInputWrapper
      label={label}
      suppressGrid={suppressGrid}
      size={size}
      required={required}
      helperText={helperText}
    >
      <Field
        name={name}
        parse={parseFloatValue}
        format={formatFloat}
        validate={validate}
        render={({ input, meta }) => (
          <TextInput
            {...input}
            onBlur={onBlur}
            placeholder={placeholder}
            disabled={disabled}
            error={meta.touched && !!meta.error}
            helperText={meta.touched && meta.error ? meta.error : null}
            inputMode="decimal" // decimal keyboard on mobile
            type="text" // for suffix/prefix display
            sx={sx}
            inputProps={{
              min,
              max,
              step: 1 / Math.pow(10, precision),
            }}
          />
        )}
      />
    </RffInputWrapper>
  );
};

export default RffFloatField;
